/**
 * AP Invoices Demo — early-payment discount approval banner.
 *
 * THROWAWAY DEMO CODE. When an invoice is scheduled early to capture a terms
 * discount (e.g. 2/10 Net 30), this banner surfaces the saving and asks the
 * user to approve. Approving persists to the demo store and rewrites the
 * invoice's scheduled-payment explanation, then invalidates the invoice queries
 * so the metadata panel reflects the approved payment. Renders only in demo
 * mode for invoices that carry a scheduled early payment.
 */

import type { APInvoice } from '@erp/rpc';
import {
  SparkleIcon,
  CheckCircleIcon,
  ChartLineUpIcon,
} from '@phosphor-icons/react';
import { T, Var } from 'gt-react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { orpc } from '@/api/rpc/client';
import { formatDateOnlyString } from '@/utils/dateUtils';
import { isApDemoMode } from './demo-mode';
import {
  applyEarlyPayApproval,
  formatCurrency,
  parseDiscountRate,
} from './early-pay';
import { updateApDemoState } from './fixture-store';
import { useApEarlyPayApproved } from './provenance';

export function EarlyPayApprovalBanner({ invoice }: { invoice: APInvoice }) {
  const queryClient = useQueryClient();
  const approved = useApEarlyPayApproved(invoice.id);

  if (!isApDemoMode()) return null;
  if (!invoice.scheduledPaymentDate || !invoice.scheduledPaymentExplanation) {
    return null;
  }

  const rate = parseDiscountRate(invoice.paymentTerms);
  if (rate <= 0) return null;

  const currency = invoice.currency || 'USD';
  // Base the discount on the (stable) pre-discount subtotal + tax, NOT the
  // totalAmount — approval rewrites totalAmount, and we must not re-discount it.
  const baseTotal =
    Number(invoice.subtotal ?? 0) + Number(invoice.taxAmount ?? 0);
  const original = baseTotal > 0 ? baseTotal : Number(invoice.totalAmount);
  const discounted = original * (1 - rate);
  const saving = original - discounted;
  // The invoice is currently scheduled near its due date; the agent recommends
  // moving the payment up to the preferred (early-discount) date.
  const scheduledLabel = formatDateOnlyString(invoice.scheduledPaymentDate);
  const earlyDate = invoice.preferredPayDate ?? invoice.scheduledPaymentDate;
  const earlyLabel = formatDateOnlyString(earlyDate);
  const handleApprove = () => {
    updateApDemoState((s) => {
      applyEarlyPayApproval(s, invoice.id);
    });

    // Refresh the invoice so the new total + discount line show immediately.
    void queryClient.invalidateQueries({
      queryKey: orpc.accountsPayable.invoices.get.key(),
    });
    void queryClient.invalidateQueries({
      queryKey: orpc.accountsPayable.invoices.list.key(),
    });
  };

  if (approved) {
    return (
      <div className="mx-1 flex items-center gap-2 rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
        <CheckCircleIcon
          className="size-5 shrink-0 text-emerald-600"
          weight="fill"
        />
        <span className="flex flex-wrap items-center gap-1">
          <T>
            Early payment approved — scheduled for <Var>{scheduledLabel}</Var>{' '}
            at
          </T>
          <span className="font-semibold tabular-nums">
            {formatCurrency(discounted, currency)}
          </span>
          <span className="tabular-nums text-emerald-700/60 line-through">
            {formatCurrency(original, currency)}
          </span>
        </span>
      </div>
    );
  }

  return (
    <div className="mx-1 rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2.5">
      <div className="flex items-start gap-2">
        <SparkleIcon
          className="mt-0.5 size-5 shrink-0 text-emerald-600"
          weight="fill"
        />
        <div className="flex-1 space-y-1.5">
          <div className="text-sm font-semibold text-emerald-900">
            <T>
              Early-payment discount available —{' '}
              <Var>{Math.round(rate * 100)}</Var>% off
            </T>
          </div>
          <div className="text-xs text-emerald-800">
            <T>
              Currently scheduled for <Var>{scheduledLabel}</Var>. Pay by{' '}
              <Var>{earlyLabel}</Var> to capture this discount.
            </T>
          </div>
          {/* Original price struck through + the discount line + new total. */}
          <div className="space-y-0.5 text-xs tabular-nums">
            <div className="flex items-center justify-between gap-4 text-emerald-700/70">
              <span>
                <T>Invoice total</T>
              </span>
              <span className="line-through">
                {formatCurrency(original, currency)}
              </span>
            </div>
            <div className="flex items-center justify-between gap-4 text-emerald-700">
              <span>
                <T>
                  Early-payment discount (<Var>{Math.round(rate * 100)}</Var>%)
                </T>
              </span>
              <span>−{formatCurrency(saving, currency)}</span>
            </div>
            <div className="flex items-center justify-between gap-4 border-t border-emerald-300 pt-0.5 font-semibold text-emerald-900">
              <span>
                <T>You pay</T>
              </span>
              <span>{formatCurrency(discounted, currency)}</span>
            </div>
          </div>
          <div className="flex items-start gap-1.5 text-xs text-emerald-700">
            <ChartLineUpIcon
              className="mt-0.5 size-3.5 shrink-0"
              weight="bold"
            />
            <span>
              <T>
                Cash Flow Projection confirms there's room to pay early safely.
              </T>
            </span>
          </div>
        </div>
        <div className="flex shrink-0 gap-2">
          <Button
            size="sm"
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            onClick={handleApprove}
          >
            <T context="approve scheduling an early discounted payment">
              Approve early payment
            </T>
          </Button>
        </div>
      </div>
    </div>
  );
}
